import { useCallback, useEffect, useState, useRef } from 'react';
import { BackendEvent, SessionEvent, SessionEventTypes } from '../../../../../../../../models/BackendEvents';
import { getClassNames } from '../../../../../../../../helpers/classHelpers';
import styles from "./TenderAssistantRequirementsViewer.module.scss";
import {
    TenderRequirement,
    ExtractionComplete,
    StreamingComplete,
    confidenceIndicators
} from './types';

const TenderAssistantStreamingViewer = ({
    events,
    onComplete
}: {
    documentId?: string;
    chatId: string;
    events: BackendEvent[];
    onComplete: (artifactId: string) => void;
}) => {
    const [streamedRequirements, setStreamedRequirements] = useState<TenderRequirement[]>([]);
    const [isExtractionComplete, setIsExtractionComplete] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const tableContainerRef = useRef<HTMLDivElement>(null);


    const shouldAutoScroll = useCallback(() => {
        const container = tableContainerRef.current;
        if (!container) return false;

        const { scrollTop, scrollHeight, clientHeight } = container;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        return distanceFromBottom <= 50;
    }, []);

    const scrollToBottom = useCallback(() => {
        const container = tableContainerRef.current;
        if (container) {
            container.scrollTo({
                top: container.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, []);

    const scrollBottomRef = useRef<ReturnType<typeof setTimeout>>();
    useEffect(() => {
        const streaming = events.filter(x => x.type === SessionEventTypes.workflowStreaming && x.metadata?.streaming_type === 'tender-assistant' && x.metadata.streaming_data) as SessionEvent[];
        const requirements = streaming.filter(x => x.metadata.streaming_event === 'requirement-streamed').map(x => x.metadata.streaming_data.requirement) as TenderRequirement[];
        setStreamedRequirements(requirements);

        const shouldScroll = shouldAutoScroll();
        if (shouldScroll) {
            clearTimeout(scrollBottomRef.current);
            scrollBottomRef.current = setTimeout(() => scrollToBottom(), 100); // Small delay to ensure DOM update
        }

        const extractionCompleteEvent = streaming.find(x => x.metadata.streaming_event === 'extraction-complete')?.metadata.streaming_data as ExtractionComplete;
        const completeEvent = streaming.find(x => x.metadata.streaming_event === 'streaming-complete')?.metadata.streaming_data as StreamingComplete;
        setIsExtractionComplete(!!extractionCompleteEvent);
        setIsComplete(!!completeEvent);
        if (completeEvent) {
            setTimeout(() => {
                onComplete(completeEvent.artifact_id);
            }, 1000);
        }
    }, [events, shouldAutoScroll, scrollToBottom, onComplete]);


    if (isComplete) {
        return (
            <div className={styles.streamingViewer}>
                <div className={getClassNames([styles.progress, styles.complete])}>
                    Processing complete! Found {streamedRequirements.length} requirements. Loading editor...
                </div>
            </div>
        );
    }

    const getColumnHeaders = () => {
        if (streamedRequirements.length === 0) return [];

        const sampleReq = streamedRequirements[0];
        const headers = [];

        if (sampleReq.page_index !== undefined) headers.push('Page');
        if (sampleReq.hierarchical_reference_number) headers.push('Reference #');
        if (sampleReq.product_reference?.product_name) headers.push('Product Name');
        if (sampleReq.number_of_units !== undefined) headers.push('Quantity');
        if (sampleReq.unit_of_measurement) headers.push('Unit');
        if (sampleReq.product_matches?.length > 0) headers.push('Best Match');
        if (sampleReq.product_specifications && sampleReq.product_specifications.length > 0) headers.push('Specifications');
        if (sampleReq.additional_info) headers.push('Additional Info');

        return headers;
    };

    const renderCellValue = (req: TenderRequirement, header: string) => {
        switch (header) {
            case 'Page':
                return req.page_index;
            case 'Reference #':
                return req.hierarchical_reference_number || '';
            case 'Product Name':
                return req.product_reference?.product_name || '';
            case 'Quantity':
                return req.number_of_units || '';
            case 'Unit':
                return req.unit_of_measurement || '';
            case 'Best Match':
                if (req.product_matches?.length > 0) {
                    const match = req.product_matches[0];
                    return (
                        <span>
                            {confidenceIndicators[match.confidence || 'none']} {match.product_id}
                        </span>
                    );
                }
                return '';
            case 'Specifications':
                if (req.product_specifications && req.product_specifications.length > 0) {
                    return (
                        <div className={styles.specificationsList}>
                            {req.product_specifications.slice(0, 3).map((spec, specIdx) => (
                                <div key={specIdx} className={styles.specificationItem}>
                                    {spec.attribute_type}: {spec.attribute_value}
                                </div>
                            ))}
                            {req.product_specifications.length > 3 && (
                                <div className={styles.specificationItem}>
                                    <em>+{req.product_specifications.length - 3} more...</em>
                                </div>
                            )}
                        </div>
                    );
                }
                return '';
            case 'Additional Info':
                const additionalInfo = req.additional_info || '';
                if (additionalInfo.length > 150) {
                    const remainingChars = additionalInfo.length - 150;
                    return (
                        <span title={additionalInfo}>
                            {additionalInfo.substring(0, 150)}... <em>(+{remainingChars})</em>
                        </span>
                    );
                }
                return additionalInfo;
            default:
                return '';
        }
    };

    const columnHeaders = getColumnHeaders();
    return (
        <div className={styles.streamingViewer}>
            <div className={styles.progress}>
                <div>
                    {isExtractionComplete
                        ? `All ${streamedRequirements.length} requirements extracted, finalizing product matching...`
                        : `Processing requirements... Found ${streamedRequirements.length} so far`
                    }
                </div>
                <div className={styles.progressBar}>
                    <div
                        className={styles.progressFill}
                        style={{
                            width: '100%',
                            animation: 'pulse 1.5s ease-in-out infinite'
                        }}
                    />
                </div>
            </div>

            <div className={styles.tableContainer} ref={tableContainerRef}>
                {columnHeaders.length > 0 && (
                    <table className={styles.streamingTable}>
                        <thead>
                            <tr>
                                {columnHeaders.map(header => (
                                    <th key={header}>{header}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {streamedRequirements.map((req, idx) => (
                                <tr key={idx} className={styles.streamedRow}>
                                    {columnHeaders.map(header => (
                                        <td key={header}>
                                            {renderCellValue(req, header)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {streamedRequirements.length === 0 && (
                    <div className={styles.loadingMessage}>
                        Starting requirements extraction...
                    </div>
                )}
            </div>
        </div>
    );
};

export default TenderAssistantStreamingViewer;